import { getVersion, isPlatformWeb } from '../utilities'
import i18n from '../i18n'
import Logger from '../services/logger.service'
import { ZollApiErrorResponse, Message } from '../features/zollApi/store/zollApi.types'
import { EndOfSupportService } from '../features/login/services/endOfSupport.service'
import { HttpCodes } from '../common/httpCodes.constants'

export class ZollAPIService {
  private authToken: string

  static readonly DISPLAY_ALERT_LOG = [
    { id: 3, name: 'Alert' },
    { id: 1, name: 'Log' },
  ]
  static readonly DISPLAY_TOASTR = [{ id: 2, name: 'Toastr' }]
  static readonly DISPLAY_TOASTR_LOG = [
    { id: 2, name: 'Toastr' },
    { id: 1, name: 'Log' },
  ]

  constructor(authToken: string) {
    this.authToken = authToken
  }

  addJwtToHeaders = (options: any, addJwtToHeadersFlag?: boolean): any => {
    if (addJwtToHeadersFlag === true || typeof addJwtToHeadersFlag === 'undefined') {
      options.headers['Authorization'] = `Bearer ${this.authToken}`
    }
    return options
  }

  handleResponse = (response: any) => {
    if (response.status >= HttpCodes.SUCCESS_200 && response.status < HttpCodes.BAD_REQUEST_400) {
      return response
    } else if (response.status === HttpCodes.UNAUTHORIZED_401 && response.headers?.has('www-authenticate')) {
      return ZollAPIService.createInvalidAuthenticationResponse()
    } else if (response.status === HttpCodes.SERVICE_UNAVAILABLE_503 && response.messages && response.messages.length) {
      // Server is not available (End Of Support has been reached)
      EndOfSupportService.crudEndOfSupportForVersion(response.messages[0].message)
      return response
    } else if (response.status === HttpCodes.VERSION_NOT_SUPPORTED_505) {
      return ZollAPIService.createClientServerVersionMismatchResponse()
    } else if (response.status === HttpCodes.NOTVALID_403 && response.authRestrictionType === 'IP_RESTRICTION') {
      return ZollAPIService.handleInValidIpResponse(response)
    } else if (response.status === HttpCodes.NOTVALID_403 && response.authRestrictionType === 'DEVICE_RESTRICTION') {
      return ZollAPIService.normalizeDeviceRestrictionResponse(response)
    } else {
      if (response.messages && response.messages.length) {
        return response
      } else {
        return ZollAPIService.createFallbackResponse(response)
      }
    }
  }

  async get(url: string, addJwtToHeadersFlag?: boolean): Promise<any> {
    const options = {
      method: 'GET',
      headers: {
        version: getVersion(),
      },
    }

    this.addJwtToHeaders(options, addJwtToHeadersFlag)

    Logger.apiCall('GET', url)
    return await fetch(url, options)
      .then((response) => {
        return response
          .json()
          .then((json) =>
            this.handleResponse({
              ...json,
              authenticationJwt: response.headers.get('authentication'),
              authRestrictionType: response.headers.get('X-Auth-Restriction-Type'),
            })
          )
          .catch(() => this.handleResponse(response))
      })
      .catch((error) => this.handleResponse(error))
  }

  async post(url: string, body: any, addJwtToHeadersFlag?: boolean, signal?: AbortSignal): Promise<any> {
    const options = {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        version: getVersion(),
      },
      body: JSON.stringify({
        body,
      }),
      signal,
    }

    this.addJwtToHeaders(options, addJwtToHeadersFlag)

    Logger.apiCall('POST', url)
    return await fetch(url, options)
      .then((response) => {
        return response
          .json()
          .then((json) =>
            this.handleResponse({
              ...json,
              authenticationJwt: response.headers.get('authentication'),
              authRestrictionType: response.headers.get('X-Auth-Restriction-Type'),
            })
          )
          .catch(() => this.handleResponse(response))
      })
      .catch((error) => {
        if (error?.name === 'AbortError') throw error
        return this.handleResponse(error)
      })
  }

  async postWithError(url: string, body: any, addJwtToHeadersFlag?: boolean): Promise<any> {
    const options = {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        version: getVersion(),
        isPWA: isPlatformWeb().toString(),
      },
      body: JSON.stringify({
        body,
      }),
    }

    this.addJwtToHeaders(options, addJwtToHeadersFlag)

    Logger.apiCall('POST', url)
    return await fetch(url, options)
      .then((response) => {
        return response
          .json()
          .then((json) =>
            this.handleResponse({
              ...json,
              authenticationJwt: response.headers.get('authentication'),
              authRestrictionType: response.headers.get('X-Auth-Restriction-Type'),
            })
          )
          .catch(() => this.handleResponse(response))
      })
      .catch((error) => this.handleResponse(error))
  }

  static createMockErrorResponse = (status: number, message: string): ZollApiErrorResponse => {
    const mockErrorResponse: ZollApiErrorResponse = {
      status,
      messages: [
        {
          message,
          status,
          display: ZollAPIService.DISPLAY_TOASTR,
        },
      ],
    }

    return mockErrorResponse
  }

  static createToastLogErrorResponse = (message: string) => ({
    status: null,
    messages: [
      {
        status: null,
        display: [
          { id: 2, name: 'Toastr' },
          { id: 1, name: 'Log' },
        ],
        message,
      },
    ],
  })

  static createInvalidAuthenticationResponse = () => {
    const errorResponse = {
      status: HttpCodes.UNAUTHORIZED_401,
      messages: [
        {
          status: HttpCodes.UNAUTHORIZED_401,
          display: ZollAPIService.DISPLAY_ALERT_LOG,
          message: 'Invalid Authentication token: You must log back in.',
        },
      ],
      callback: 'handleBadJwt',
    }

    return errorResponse
  }

  static normalizeDeviceRestrictionResponse = (response: ZollApiErrorResponse) => {
    return {
      ...response,
      messages: response.messages?.map((m: Message) => ({
        ...m,
        message: i18n.t('serviceSelection.deviceRestrictionError'),
        status: m.status ?? HttpCodes.NOTVALID_403,
        display: ZollAPIService.DISPLAY_ALERT_LOG,
        buttons: [{ text: 'Ok', callback: 'handleAuthFailureLogout' }],
      })),
    }
  }

  static handleInValidIpResponse = (response: any) => {
    const errorResponse = {
      status: HttpCodes.NOTVALID_403,
      messages: [
        {
          status: HttpCodes.NOTVALID_403,
          display: ZollAPIService.DISPLAY_ALERT_LOG,
          message: `Your IP address: ${response?.messages[0]?.message} is not registered for this Service. Please contact your Administrator.`,
          buttons: [{ text: 'Ok', callback: 'handleInvalidIp' }],
        },
      ],
    }

    return errorResponse
  }

  static createClientServerVersionMismatchResponse = () => {
    const errorResponse = {
      status: HttpCodes.VERSION_NOT_SUPPORTED_505,
      messages: [
        {
          status: HttpCodes.VERSION_NOT_SUPPORTED_505,
          display: ZollAPIService.DISPLAY_TOASTR,
          message: 'Client - Server Version Mismatch: Please update the app to the latest version.',
        },
      ],
    }

    return errorResponse
  }

  static createFallbackResponse = (response: any) => {
    if (response && response.status && response.error) {
      const errorResponse = {
        status: response.status,
        messages: [
          {
            status: response.status,
            display: ZollAPIService.DISPLAY_TOASTR_LOG,
            message: `${response.error?.message} Stack Trace: ${response.error?.stackTrace} Cause: ${response.error?.cause}`,
          },
        ],
      }

      return errorResponse
    }

    // If we get here, the server was not even reachable
    // Are we in EOS?
    const eosMessageForVersion = EndOfSupportService.getEosMessageForVersion()
    const fallBackMessage =
      'Unable to contact the server.  If this issue persists, please contact ZOLL customer support.  If necessary, you can continue documenting with emsCharts NOW in an offline state.'

    const message = eosMessageForVersion ? eosMessageForVersion : fallBackMessage

    const errorResponse = {
      status: HttpCodes.NOT_FOUND_404,
      messages: [
        {
          status: HttpCodes.NOT_FOUND_404,
          display: ZollAPIService.DISPLAY_TOASTR_LOG,
          message: message,
        },
      ],
    }

    return errorResponse
  }
}
